import { COLLECTION_ZIP_MAX_IMAGES, getAPIErrorCode, useDownloadCollectionImagesMutation } from '@vizit/api';
import { DOWNLOAD_FAILED_TITLE, type ToastCopy, useZipDownload } from '@vizit/organisms';
import { plural } from '@vizit/utils';
import * as React from 'react';

/** "Bundling 5 images from Lifestyle & In-Use…", the same for the selection and the whole collection (SDD D77). */
const bundling = (count: number, collectionName: string) =>
  `Bundling ${plural(count, 'image')} from ${collectionName}. Your .zip will start downloading shortly.`;

/** The failure toast for an API error code (SDD 3.3), or the generic one. */
function describeDownloadError(error: unknown): ToastCopy | undefined {
  switch (getAPIErrorCode(error)) {
    case 'DOWNLOAD_LIMIT_EXCEEDED':
      return {
        title: DOWNLOAD_FAILED_TITLE,
        description: `Select up to ${COLLECTION_ZIP_MAX_IMAGES} images to download at once.`,
      };
    case 'IMAGES_NOT_DOWNLOADABLE':
      return {
        title: DOWNLOAD_FAILED_TITLE,
        description: 'Some selected images are no longer in this collection. Refresh the page and try again.',
      };
    case 'INVALID_OFFSET':
    case 'OFFSET_NOT_SUPPORTED':
      return {
        title: "Couldn't download the whole collection",
        description: 'The collection changed while it was downloading. Please try again.',
      };
    default:
      return undefined;
  }
}

interface CollectionDownloadTarget {
  workspaceId: string;
  collectionId: string;
  /** Named in the toasts, e.g. "Bundling 5 images from Lifestyle & In-Use." */
  collectionName: string;
}

interface DownloadImagesOptions extends CollectionDownloadTarget {
  /** Up to 200, the action bar disables Download above that (SDD D49). */
  imageIds: string[];
}

interface DownloadCollectionOptions extends CollectionDownloadTarget {
  /** Images in the collection as the card shows it, for the "Preparing" toast. */
  imageCount: number;
}

/**
 * Downloads images of a collection as zips with the Bulk Download handoff toasts (SDD D77): the selected images from
 * the action bar (flow 03), or the whole collection from its card menu (flow 02), 200 images per part, one file each
 * (D50). Originals only, Spark images too (D73).
 *
 * @example
 * const { downloadImages, downloadCollection } = useCollectionDownload();
 * downloadImages({ workspaceId, collectionId, collectionName, imageIds });
 */
function useCollectionDownload() {
  const { mutateAsync: downloadCollectionImages } = useDownloadCollectionImagesMutation();
  const { download } = useZipDownload();

  const downloadImages = React.useCallback(
    ({ workspaceId, collectionId, collectionName, imageIds }: DownloadImagesOptions) =>
      download({
        fetchZip: () =>
          downloadCollectionImages({ workspace_id: workspaceId, collection_id: collectionId, image_ids: imageIds }),
        fallbackFileName: 'collection_images.zip',
        preparing: bundling(imageIds.length, collectionName),
        ready: `${collectionName} — ${plural(imageIds.length, 'image')}.`,
        empty: 'None of the original images could be found.',
        describeError: describeDownloadError,
      }),
    [download, downloadCollectionImages],
  );

  const downloadCollection = React.useCallback(
    ({ workspaceId, collectionId, collectionName, imageCount }: DownloadCollectionOptions) =>
      download({
        fetchZip: async (offset) => {
          const zip = await downloadCollectionImages({
            workspace_id: workspaceId,
            collection_id: collectionId,
            offset,
          });
          // Without `Vizit-Total-Count` the card's count still says how many parts to fetch
          return { ...zip, totalCount: zip.totalCount ?? imageCount };
        },
        partSize: COLLECTION_ZIP_MAX_IMAGES,
        fallbackFileName: 'collection_images.zip',
        preparing: bundling(imageCount, collectionName),
        preparingPart: `Bundling the next images from ${collectionName}. Each part downloads as its own .zip.`,
        ready: ({ totalCount, files }) =>
          `${collectionName} — ${plural(totalCount ?? imageCount, 'image')}${files > 1 ? ` in ${files} files` : ''}.`,
        empty: `${collectionName} has no original images to download.`,
        describeError: describeDownloadError,
      }),
    [download, downloadCollectionImages],
  );

  return { downloadImages, downloadCollection };
}

export { useCollectionDownload };
